import { useTranslation } from "react-i18next";
import { Control } from "react-hook-form";
import { FormGroup, ActionableNotification } from "@carbon/react";
import TextField from "../../../components/carbon/Form/TextField";
import SelectField from "../../../components/carbon/Form/SelectField";
import { CountryResponse } from "../../../types";
import { RegisterFormValues } from "../types";

export interface CompanyInfoSectionProps {
  control: Control<RegisterFormValues>;
  countries: CountryResponse[] | undefined;
  isCountriesError: boolean;
  onRetryCountries: () => void;
}

/**
 * Company-information field group: companyName / commercialName / taxId / country.
 * `country` is the shared `SelectField` fed by `usePublicCountries` (`GET /country/public`)
 * — which gives it a real `<label>` (accessible name "Country"), closing the pre-Carbon
 * markup's unnamed-`<select>` a11y gap the e2e net located via `#country`.
 */
const CompanyInfoSection = ({
  control,
  countries,
  isCountriesError,
  onRetryCountries,
}: CompanyInfoSectionProps) => {
  const { t } = useTranslation();

  const countryOptions = (countries ?? []).map((country) => ({
    value: String(country.id),
    label: country.name,
  }));

  return (
    <FormGroup legendText={t("register.form.companyData")}>
      <div className="register__grid">
        <TextField
          name="companyName"
          label={t("register.form.companyName")}
          control={control}
          required
        />
        <TextField
          name="commercialName"
          label={t("register.form.commercialName")}
          control={control}
          required
        />
        <TextField
          name="taxId"
          label={t("register.form.tax")}
          control={control}
          required
        />
        <SelectField
          name="country"
          label={t("companyProfile.country")}
          control={control}
          options={countryOptions}
          placeholder={t("companyProfile.selectCountry")}
        />
      </div>
      {isCountriesError ? (
        <ActionableNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t("register.form.loadCountries")}
          actionButtonLabel={t("register.form.retry")}
          onActionButtonClick={onRetryCountries}
        />
      ) : null}
    </FormGroup>
  );
};

export default CompanyInfoSection;
